import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { createProduct, deleteProduct } from '../../features/products/productSlice';

// Get user from localStorage
const userInfoFromStorage = localStorage.getItem('userInfo')
    ? JSON.parse(localStorage.getItem('userInfo'))
    : null;

const initialState = {
    userInfo: userInfoFromStorage,
    userProfile: null, // For visiting other users' profiles
    status: 'idle',
    error: null,
    followersList: [],
    followingList: [],
};

// Login user
export const login = createAsyncThunk('user/login', async (userData, { rejectWithValue }) => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json',
            },
        };
        const { data } = await axios.post('/api/users/login', userData, config);
        localStorage.setItem('userInfo', JSON.stringify(data));
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
});

// Register user
// Register user (Initiate)
export const register = createAsyncThunk('user/register', async (userData, { rejectWithValue }) => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json',
            },
        };
        const { data } = await axios.post('/api/users', userData, config);
        // Do NOT set userInfo here, as we need to verify OTP first
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
});

// Logout user
export const logout = createAsyncThunk('user/logout', async () => {
    localStorage.removeItem('userInfo');
    return null;
});

// Get User Profile By ID (Public/Private view)
export const getUserProfileById = createAsyncThunk(
    'user/getUserProfileById',
    async (id, { getState, rejectWithValue }) => {
        try {
            const {
                user: { userInfo },
            } = getState();

            let config = {};
            if (userInfo && userInfo.token) {
                config = {
                    headers: {
                        Authorization: `Bearer ${userInfo.token}`,
                    },
                };
            }

            const { data } = await axios.get(`/api/users/${id}`, config);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message
            );
        }
    }
);

// Update User Profile
export const updateUserProfile = createAsyncThunk(
    'user/updateUserProfile',
    async (user, { getState, rejectWithValue }) => {
        try {
            const {
                user: { userInfo },
            } = getState();

            if (!userInfo || !userInfo.token) {
                return rejectWithValue('Not authorized, please login');
            }

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            const { data } = await axios.put(`/api/users/profile`, user, config);
            localStorage.setItem('userInfo', JSON.stringify(data));
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message
            );
        }
    }
);

// Follow User
export const followUser = createAsyncThunk(
    'user/followUser',
    async (id, { getState, rejectWithValue }) => {
        try {
            const {
                user: { userInfo },
            } = getState();

            if (!userInfo || !userInfo.token) {
                return rejectWithValue('Not authorized, please login');
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            await axios.put(`/api/users/${id}/follow`, {}, config);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message
            );
        }
    }
);

// Unfollow User
export const unfollowUser = createAsyncThunk(
    'user/unfollowUser',
    async (id, { getState, rejectWithValue }) => {
        try {
            const {
                user: { userInfo },
            } = getState();

            if (!userInfo || !userInfo.token) {
                return rejectWithValue('Not authorized, please login');
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            await axios.put(`/api/users/${id}/unfollow`, {}, config);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message
            );
        }
    }
);

// Fetch User Followers
export const fetchUserFollowers = createAsyncThunk(
    'user/fetchUserFollowers',
    async (id, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(`/api/users/${id}/followers`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message
            );
        }
    }
);

// Fetch User Following
export const fetchUserFollowing = createAsyncThunk(
    'user/fetchUserFollowing',
    async (id, { rejectWithValue }) => {
        try {
            const { data } = await axios.get(`/api/users/${id}/following`);
            return data;
        } catch (error) {
            return rejectWithValue(
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message
            );
        }
    }
);

// Remove Follower
export const removeFollower = createAsyncThunk(
    'user/removeFollower',
    async ({ userId, followerId }, { getState, rejectWithValue }) => {
        try {
            const {
                user: { userInfo },
            } = getState();

            if (!userInfo || !userInfo.token) {
                return rejectWithValue('Not authorized, please login');
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            await axios.put(`/api/users/${userId}/removefollower`, { followerId }, config);
            return followerId;
        } catch (error) {
            return rejectWithValue(
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message
            );
        }
    }
);

// Create Business Review
export const createBusinessReview = createAsyncThunk(
    'user/createBusinessReview',
    async ({ userId, review }, { getState, rejectWithValue }) => {
        try {
            const {
                user: { userInfo },
            } = getState();

            if (!userInfo || !userInfo.token) {
                return rejectWithValue('Not authorized, please login');
            }

            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            await axios.post(`/api/users/${userId}/reviews`, review, config);
        } catch (error) {
            return rejectWithValue(
                error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message
            );
        }
    }
);

// Google Login
export const googleLogin = createAsyncThunk('user/googleLogin', async (token, { rejectWithValue }) => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json',
            },
        };
        const { data } = await axios.post('/api/users/google-login', { token }, config);
        localStorage.setItem('userInfo', JSON.stringify(data));
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
});

// Send Login OTP
export const sendLoginOtp = createAsyncThunk('user/sendLoginOtp', async (email, { rejectWithValue }) => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json',
            },
        };
        const { data } = await axios.post('/api/users/send-otp', { email }, config);
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
});

// Verify Login OTP
export const verifyLoginOtp = createAsyncThunk('user/verifyLoginOtp', async (otpData, { rejectWithValue }) => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json',
            },
        };
        const { data } = await axios.post('/api/users/verify-otp', otpData, config);
        localStorage.setItem('userInfo', JSON.stringify(data));
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
});

// Verify Register OTP (Same endpoint, different action for state management)
export const verifyRegisterOtp = createAsyncThunk('user/verifyRegisterOtp', async (otpData, { rejectWithValue }) => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json',
            },
        };
        const { data } = await axios.post('/api/users/verify-registration', otpData, config);
        localStorage.setItem('userInfo', JSON.stringify(data));
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
});

// Forgot Password
export const forgotPassword = createAsyncThunk('user/forgotPassword', async (email, { rejectWithValue }) => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json',
            },
        };
        const { data } = await axios.post('/api/users/forgot-password', { email }, config);
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
});

// Reset Password
export const resetPassword = createAsyncThunk('user/resetPassword', async (resetData, { rejectWithValue }) => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json',
            },
        };
        const { data } = await axios.put(`/api/users/reset-password/${resetData.token}`, { password: resetData.password }, config);
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
}
);



// Send Change Password OTP
export const sendChangePasswordOtp = createAsyncThunk('user/sendChangePasswordOtp', async (_, { getState, rejectWithValue }) => {
    try {
        const {
            user: { userInfo },
        } = getState();

        const config = {
            headers: {
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.post('/api/users/profile/send-password-otp', {}, config);
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
});

// Update Password with OTP
export const updatePasswordWithOtp = createAsyncThunk('user/updatePasswordWithOtp', async (passwordData, { getState, rejectWithValue }) => {
    try {
        const {
            user: { userInfo },
        } = getState();

        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userInfo.token}`,
            },
        };

        const { data } = await axios.put('/api/users/profile/password', passwordData, config);
        return data;
    } catch (error) {
        return rejectWithValue(
            error.response && error.response.data.message
                ? error.response.data.message
                : error.message
        );
    }
});

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        resetStatus: (state) => {
            state.status = 'idle';
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Login
            .addCase(login.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(login.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.userInfo = action.payload;
            })
            .addCase(login.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Register
            .addCase(register.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(register.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Do not set userInfo, just status
            })
            .addCase(register.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Verify Register OTP
            .addCase(verifyRegisterOtp.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(verifyRegisterOtp.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.userInfo = action.payload;
            })
            .addCase(verifyRegisterOtp.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Logout
            .addCase(logout.fulfilled, (state) => {
                state.userInfo = null;
                state.userProfile = null;
            })
            // Get User Profile By ID
            .addCase(getUserProfileById.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getUserProfileById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.userProfile = action.payload;
            })
            .addCase(getUserProfileById.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Update User Profile
            .addCase(updateUserProfile.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateUserProfile.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.userInfo = action.payload;
                // Also update userProfile if it matches the logged in user
                if (state.userProfile && state.userProfile._id === action.payload._id) {
                    state.userProfile = action.payload;
                }
            })
            .addCase(updateUserProfile.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Follow User

            // Follow User (Optimistic Update)
            // Follow User (Optimistic Update)
            .addCase(followUser.pending, (state, action) => {
                const userIdToFollow = action.meta.arg;
                // Update userProfile if we are viewing the followed user
                if (state.userProfile && String(state.userProfile._id) === String(userIdToFollow)) {
                    if (state.userProfile.followers && !state.userProfile.followers.some(f => String(f._id || f) === String(state.userInfo._id))) {
                        // Push full user object
                        state.userProfile.followers.push({
                            _id: state.userInfo._id,
                            name: state.userInfo.name,
                            username: state.userInfo.username,
                            profilePicture: state.userInfo.profilePicture
                        });
                    }
                }
                // Update userInfo.following
                if (state.userInfo) {
                    if (!state.userInfo.following) {
                        state.userInfo.following = [];
                    }
                    if (!state.userInfo.following.some(f => String(f._id || f) === String(userIdToFollow))) {
                        // We don't have the full object of the user we just followed easily available here unless we pass it.
                        // For now, we can try to find it in userProfile if available, or just push ID as a fallback (though risky if mixed).
                        // Better: The payload of followUser usually just returns ID.
                        // Let's assume for userInfo.following we might just store objects.
                        // If we don't have the object, we can't push it.
                        // BUT, usually userInfo.following is used to check "am I following this user?".
                        // If we change it to objects, checking validitity becomes `some(u => u._id === id)`.
                        // Ideally we should push an object. We can check if `state.userProfile` is the user we are following, then we have the object.
                        let userToFollowParams = {};
                        if (state.userProfile && String(state.userProfile._id) === String(userIdToFollow)) {
                            userToFollowParams = {
                                _id: state.userProfile._id,
                                name: state.userProfile.name,
                                username: state.userProfile.username,
                                profilePicture: state.userProfile.profilePicture
                            };
                        } else {
                            // Fallback dummy or ID if we strictly need it, but mixed array is bad.
                            // However, we mostly use it for `isFollowing` check.
                            userToFollowParams = { _id: userIdToFollow };
                        }
                        state.userInfo.following.push(userToFollowParams);
                        localStorage.setItem('userInfo', JSON.stringify(state.userInfo));
                    }
                }
            })
            // Follow User (Fulfilled)
            .addCase(followUser.fulfilled, (state, action) => {
                const userIdToFollow = action.payload; // Payload is ID
                // Update userProfile if we are viewing the followed user
                if (state.userProfile && String(state.userProfile._id) === String(userIdToFollow)) {
                    if (state.userProfile.followers && !state.userProfile.followers.some(f => String(f._id || f) === String(state.userInfo._id))) {
                        state.userProfile.followers.push({
                            _id: state.userInfo._id,
                            name: state.userInfo.name,
                            username: state.userInfo.username,
                            profilePicture: state.userInfo.profilePicture
                        });
                    }
                }
                // Update userInfo.following check
                if (state.userInfo) {
                    if (!state.userInfo.following) {
                        state.userInfo.following = [];
                    }
                    if (!state.userInfo.following.some(f => String(f._id || f) === String(userIdToFollow))) {
                        // Same logic as pending
                        let userToFollowParams = { _id: userIdToFollow };
                        if (state.userProfile && String(state.userProfile._id) === String(userIdToFollow)) {
                            userToFollowParams = {
                                _id: state.userProfile._id,
                                name: state.userProfile.name,
                                username: state.userProfile.username,
                                profilePicture: state.userProfile.profilePicture
                            };
                        }
                        state.userInfo.following.push(userToFollowParams);
                        localStorage.setItem('userInfo', JSON.stringify(state.userInfo));
                    }
                }
            })
            // Follow User (Rejected - Rollback)
            .addCase(followUser.rejected, (state, action) => {
                const userIdToFollow = action.meta.arg;
                // Rollback userProfile
                if (state.userProfile && String(state.userProfile._id) === String(userIdToFollow)) {
                    state.userProfile.followers = state.userProfile.followers.filter(f => String(f._id || f) !== String(state.userInfo._id));
                }
                // Rollback userInfo
                if (state.userInfo && state.userInfo.following) {
                    state.userInfo.following = state.userInfo.following.filter(f => String(f._id || f) !== String(userIdToFollow));
                    localStorage.setItem('userInfo', JSON.stringify(state.userInfo));
                }
            })
            // Unfollow User

            // Unfollow User (Optimistic Update)
            .addCase(unfollowUser.pending, (state, action) => {
                const userIdToUnfollow = action.meta.arg;

                // Update userProfile if we are viewing the unfollowed user
                if (state.userProfile && String(state.userProfile._id) === String(userIdToUnfollow)) {
                    if (state.userProfile.followers) {
                        state.userProfile.followers = state.userProfile.followers.filter(f => String(f._id || f) !== String(state.userInfo._id));
                    }
                }
                // Update userInfo.following
                if (state.userInfo && state.userInfo.following) {
                    state.userInfo.following = state.userInfo.following.filter(f => String(f._id || f) !== String(userIdToUnfollow));
                    localStorage.setItem('userInfo', JSON.stringify(state.userInfo));
                }
            })
            // Unfollow User (Fulfilled)
            .addCase(unfollowUser.fulfilled, (state, action) => {
                const userIdToUnfollow = action.payload;

                // Update userProfile if we are viewing the unfollowed user
                if (state.userProfile && String(state.userProfile._id) === String(userIdToUnfollow)) {
                    if (state.userProfile.followers) {
                        state.userProfile.followers = state.userProfile.followers.filter(f => String(f._id || f) !== String(state.userInfo._id));
                    }
                }

                // Update userInfo.following
                if (state.userInfo && state.userInfo.following) {
                    state.userInfo.following = state.userInfo.following.filter(f => String(f._id || f) !== String(userIdToUnfollow));
                    localStorage.setItem('userInfo', JSON.stringify(state.userInfo));
                }

                if (state.followingList) {
                    state.followingList = state.followingList.filter(user => String(user._id) !== String(userIdToUnfollow));
                }
            })
            // Unfollow User (Rejected - Rollback)
            .addCase(unfollowUser.rejected, (state, action) => {
                const userIdToUnfollow = action.meta.arg;
                // Rollback userProfile
                if (state.userProfile && String(state.userProfile._id) === String(userIdToUnfollow)) {
                    // Check if not already there to avoid dupes on weird race
                    if (!state.userProfile.followers.some(f => String(f._id || f) === String(state.userInfo._id))) {
                        state.userProfile.followers.push({
                            _id: state.userInfo._id,
                            name: state.userInfo.name,
                            username: state.userInfo.username,
                            profilePicture: state.userInfo.profilePicture
                        });
                    }
                }
                // Rollback userInfo
                if (state.userInfo) {
                    if (!state.userInfo.following.some(f => String(f._id || f) === String(userIdToUnfollow))) {
                        // Fallback to simple object with ID
                        state.userInfo.following.push({ _id: userIdToUnfollow });
                        localStorage.setItem('userInfo', JSON.stringify(state.userInfo));
                    }
                }
            })
            // Google Login
            .addCase(googleLogin.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(googleLogin.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.userInfo = action.payload;
            })
            .addCase(googleLogin.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Verify Login OTP
            .addCase(verifyLoginOtp.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(verifyLoginOtp.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.userInfo = action.payload;
            })
            .addCase(verifyLoginOtp.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Fetch Followers
            .addCase(fetchUserFollowers.fulfilled, (state, action) => {
                state.followersList = action.payload;
            })
            // Fetch Following
            .addCase(fetchUserFollowing.fulfilled, (state, action) => {
                state.followingList = action.payload;
            })
            // Remove Follower
            .addCase(removeFollower.fulfilled, (state, action) => {
                if (state.followersList) {
                    state.followersList = state.followersList.filter(user => user._id != action.payload);
                }
                // Also update userProfile followers count if viewing own profile
                if (state.userProfile && state.userInfo && state.userProfile._id == state.userInfo._id) {
                    if (state.userProfile.followers) {
                        state.userProfile.followers = state.userProfile.followers.filter(id => id != action.payload);
                    }
                }
            })
            // Create Product - Update userProfile if viewing own profile
            .addCase(createProduct.fulfilled, (state, action) => {
                if (state.userProfile && state.userInfo && state.userProfile._id === state.userInfo._id) {
                    if (!state.userProfile.products) {
                        state.userProfile.products = [];
                    }
                    state.userProfile.products.push(action.payload);
                }
            })
            // Delete Product - Update userProfile if viewing own profile
            .addCase(deleteProduct.fulfilled, (state, action) => {
                if (state.userProfile && state.userInfo && state.userProfile._id === state.userInfo._id) {
                    if (state.userProfile.products) {
                        state.userProfile.products = state.userProfile.products.filter(p => p._id !== action.payload);
                    }
                }
            })
            // Send Change Password OTP
            .addCase(sendChangePasswordOtp.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(sendChangePasswordOtp.fulfilled, (state) => {
                state.status = 'succeeded';
            })
            .addCase(sendChangePasswordOtp.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Update Password With OTP
            .addCase(updatePasswordWithOtp.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updatePasswordWithOtp.fulfilled, (state) => {
                state.status = 'succeeded';
                state.error = null;
            })
            .addCase(updatePasswordWithOtp.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
    },
});

export const { resetStatus } = userSlice.actions;
export default userSlice.reducer;
